import React, { useContext, useRef, useState } from "react";
import RegistrationContext from "../contexts/RegistrationContext";
import SidePanel from "../components/SidePanel/SidePanel";
import UserInfo from "../pages/UserInfo/UserInfo";
import SearchPapers from "../components/SearchPapers/SearchPapers";
import Spinner from "../components/Spinner/Spinner";
import Summary from "../pages/Summary/Summary";
import Billing from "../pages/Summary/Billing";
import Extras from "../pages/Summary/Extras";
import Upgrade from "../pages/Summary/Upgrade";
import { CheckCircle2, Clock, XCircle, Download } from "lucide-react";
import axios from "axios";
import { base_url } from "../config";

const TABS = [
  { id: "summary", label: "Summary" },
  { id: "extras",  label: "Add additional papers" },
  { id: "billing", label: "Payment receipts" },
  { id: "upgrade", label: "Upgrade to Full Delegate" },
];

const useReupload = (reload, setReload) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleReupload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("studentProofFile", file);
      await axios.post(`${base_url}/api/reupload_student_proof`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReload(reload + 1);
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return { fileInputRef, uploading, error, handleReupload };
};

const RejectedHero = ({ registrationId, comment, reload, setReload }) => {
  const { fileInputRef, uploading, error, handleReupload } = useReupload(reload, setReload);

  return (
    <div className="py-10 px-6 text-center">
      <div className="flex justify-center mb-3">
        <XCircle size={52} color="#f87171" strokeWidth={1.5} />
      </div>
      <h1 className="text-white text-3xl font-bold mb-2">Student Proof Rejected</h1>
      <p className="text-red-200 text-sm mb-6">
        Your proof document could not be validated. Please upload a new one.
      </p>

      {comment && (
        <div
          className="max-w-md mx-auto mb-6 text-left rounded-xl px-5 py-4"
          style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,100,100,0.4)" }}
        >
          <p className="text-xs font-bold uppercase tracking-widest text-red-300 mb-2">Reason</p>
          <p className="text-white text-sm leading-relaxed">{comment}</p>
        </div>
      )}

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 font-semibold text-sm px-6 py-2.5 rounded-full disabled:opacity-50 transition-opacity"
        style={{ background: "#dc2626", color: "#fff", border: "none", cursor: "pointer" }}
      >
        {uploading ? "Uploading…" : "Upload new proof"}
      </button>
      <p className="text-xs mt-2" style={{ color: "rgba(255,255,255,0.45)" }}>PDF, JPG or PNG · max 4 MB</p>
      {error && <p className="text-red-300 text-xs mt-2">{error}</p>}

      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleReupload} />

      <div className="mt-8">
        <span
          className="inline-block text-white text-sm font-mono px-4 py-1.5 rounded-full"
          style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)" }}
        >
          Registration ID: {registrationId}
        </span>
      </div>
    </div>
  );
};

const PendingHero = ({ registrationId, fileName, reload, setReload }) => {
  const { fileInputRef, uploading, error, handleReupload } = useReupload(reload, setReload);

  const handleDownload = async () => {
    const res = await axios.get(`${base_url}/api/get_student_proof`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="py-10 px-6 text-center">
      <div className="flex justify-center mb-3">
        <Clock size={52} color="#fbbf24" strokeWidth={1.5} />
      </div>
      <h1 className="text-white text-3xl font-bold mb-2">Student Validation Pending</h1>
      <p className="text-blue-200 text-sm mb-6">
        Your proof document is being reviewed. You'll receive an email once it's validated.
      </p>

      <div className="flex justify-center gap-3 flex-wrap">
        {fileName && (
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center gap-2 font-semibold text-sm px-6 py-2.5 rounded-full transition-opacity"
            style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", cursor: "pointer" }}
          >
            <Download size={15} /> View uploaded proof
          </button>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 font-semibold text-sm px-6 py-2.5 rounded-full disabled:opacity-50 transition-opacity"
          style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer" }}
        >
          {uploading ? "Uploading…" : "Replace document"}
        </button>
      </div>
      <p className="text-xs mt-2" style={{ color: "rgba(255,255,255,0.45)" }}>PDF, JPG or PNG · max 4 MB</p>
      {error && <p className="text-red-300 text-xs mt-2">{error}</p>}

      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleReupload} />

      <div className="mt-8">
        <span
          className="inline-block text-white text-sm font-mono px-4 py-1.5 rounded-full"
          style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)" }}
        >
          Registration ID: {registrationId}
        </span>
      </div>
    </div>
  );
};

const RegistrationComplete = () => {
  const {
    loading,
    data,
    type,
    canRegisterPapers,
    reload,
    setReload,
    userInfoOpen,
    setUserInfoOpen,
    paperOpen,
    setPaperOpen,
  } = useContext(RegistrationContext);

  const [activeTab, setActiveTab] = useState("summary");

  const validationState = data?.student_validation_state ?? null;
  const isStudent = type === "student";
  // Hide the upgrade tab once upgraded (type is no longer student)
  const visibleTabs = TABS.filter((t) => {
    if (t.id === "extras") return canRegisterPapers;
    if (t.id === "upgrade") return isStudent;
    return true;
  });

  return (
    <div className="flex flex-col flex-1">
      <SidePanel isOpen={userInfoOpen} openCloseHandler={setUserInfoOpen}>
        <UserInfo />
      </SidePanel>

      <SidePanel isOpen={paperOpen} openCloseHandler={setPaperOpen}>
        <SearchPapers registrationComplete />
      </SidePanel>

      {/* Hero banner */}
      <div
        className="w-full mt-6"
        style={{ background: "linear-gradient(135deg, #002E78 0%, #004ab5 100%)" }}
      >
        {isStudent && validationState === "rejected" ? (
          <RejectedHero
            registrationId={data.id}
            comment={data.student_validation_comment}
            reload={reload}
            setReload={setReload}
          />
        ) : isStudent && validationState === "pending" ? (
          <PendingHero
            registrationId={data.id}
            fileName={data.student_proof_file_name}
            reload={reload}
            setReload={setReload}
          />
        ) : (
          <div className="py-10 px-6 text-center">
            <div className="flex justify-center mb-3">
              <CheckCircle2 size={52} color="#4ade80" strokeWidth={1.5} />
            </div>
            <h1 className="text-white text-3xl font-bold mb-2">Registration Complete!</h1>
            <p className="text-blue-200 text-sm mb-4">A confirmation email has been sent to you.</p>
            <span
              className="inline-block text-white text-sm font-mono px-4 py-1.5 rounded-full"
              style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)" }}
            >
              Registration ID: {data.id}
            </span>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex justify-center border-t border-white/20">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-8 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-white text-white"
                  : "border-transparent text-blue-200 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="bg-custombg flex-1">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        ) : (
          <div className="max-w-3xl mx-auto py-8 px-4">
            {activeTab === "summary" && <Summary />}
            {activeTab === "extras"  && canRegisterPapers && <Extras onDone={() => setActiveTab("summary")} />}
            {activeTab === "billing" && <Billing />}
            {activeTab === "upgrade" && isStudent && <Upgrade />}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationComplete;
